import { useCallback } from "react";

import { FrontendRoutes, DashboardRoutes, UrlBuilder, BuiltRoute } from "@simplyk/common";
import { usePathname, useSearchParams } from "next/navigation";

import { FRONTEND_URL } from "@/routes";

type Route = FrontendRoutes | DashboardRoutes | BuiltRoute;
type SearchParamsType = Record<string, string>;
type SearchParamsCallbackType = (searchParams: SearchParamsType) => SearchParamsType;

export const appendSearchParams = (searchParamsToAppend: SearchParamsType) => (searchParams: SearchParamsType) => {
  return {
    ...searchParams,
    ...searchParamsToAppend,
  } as SearchParamsType;
};

export const removeSearchParams =
  (...searchParamsToRemove: string[]) =>
  (searchParams: SearchParamsType) => {
    const newSearchParams = { ...searchParams };
    searchParamsToRemove.forEach((param) => {
      newSearchParams[param] = "";
    });
    return newSearchParams;
  };

export const useUrlBuilder = () => {
  const routerPathname = usePathname();
  const routerSearchParams = useSearchParams();

  return useCallback(
    ({
      route,
      hash,
      searchParams,
      includeRouterSearchParams = true,
      asExternalLink = false,
    }: {
      route?: Route;
      hash?: string;
      searchParams?: SearchParamsType | SearchParamsCallbackType;
      includeRouterSearchParams?: boolean;
      asExternalLink?: boolean;
    }) => {
      const { routePathname, routeSearchParams } = (() => {
        if (route instanceof BuiltRoute) {
          return { routePathname: route.path, routeSearchParams: route.searchParams };
        }

        if (route) {
          return { routePathname: route, routeSearchParams: {} };
        }

        return { routePathname: routerPathname, routeSearchParams: {} };
      })();

      // When running in Storybook, routerSearchParams is undefined, treat as empty object
      const routerSearchParamsEntries =
        routerSearchParams && includeRouterSearchParams
          ? { ...Object.fromEntries(routerSearchParams), ...routeSearchParams }
          : routeSearchParams;

      const searchParamsValue = Object.fromEntries(
        Object.entries(
          typeof searchParams === "function"
            ? searchParams(routerSearchParamsEntries)
            : (searchParams ?? routerSearchParamsEntries)
        ).filter(([, value]) => !!value)
      );
      const finalSearchParams = new URLSearchParams(searchParamsValue);

      const url = UrlBuilder({
        pathname: routePathname,
        hash,
        searchParams: finalSearchParams,
      }).build();

      if (asExternalLink) {
        return `${FRONTEND_URL}${url}`;
      }

      return url;
    },
    [routerPathname, routerSearchParams]
  );
};
